import type { Question, QuestionType } from '../types/skillTests';

const questionTypeCycle: QuestionType[] = ['multiple-choice', 'short-answer', 'true-false', 'long-answer'];

const fallbackTopics = [
  'candidate experience',
  'operational excellence',
  'communication',
  'quality and safety',
  'continuous improvement',
];

const toTitleCase = (word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

export function generateAiQuestions(jobSpec: string, questionCount: number): Question[] {
  const sanitizedCount = Math.max(1, Math.min(questionCount, 12));
  const rawKeywords = jobSpec
    .split(/[^a-zA-Z]+/)
    .filter((token) => token.length > 3)
    .map((token) => token.toLowerCase());
  const uniqueKeywords = Array.from(new Set(rawKeywords));
  const topics = (uniqueKeywords.length > 0 ? uniqueKeywords : fallbackTopics).map(toTitleCase);

  const baseId = Date.now();

  return Array.from({ length: sanitizedCount }).map((_, index) => {
    const type = questionTypeCycle[index % questionTypeCycle.length];
    const topic = topics[index % topics.length];

    if (type === 'multiple-choice') {
      const prompt = `How would you handle a scenario related to ${topic.toLowerCase()} while following the expectations described in the job specification?`;
      const options = [
        `Prioritise ${topic.toLowerCase()} with proactive communication.`,
        `Leave ${topic.toLowerCase()} until the end of the shift.`,
        `Escalate ${topic.toLowerCase()} issues without investigating.`,
        `Ignore ${topic.toLowerCase()} unless someone complains.`,
      ];
      return {
        id: baseId + index,
        text: prompt,
        type,
        options,
        correctAnswer: options[0],
        points: 10,
      };
    }

    if (type === 'true-false') {
      return {
        id: baseId + index,
        text: `True or false: Maintaining strong ${topic.toLowerCase()} is essential to succeed in this role.`,
        type,
        options: ['True', 'False'],
        correctAnswer: 'true',
        points: 5,
      };
    }

    if (type === 'long-answer') {
      return {
        id: baseId + index,
        text: `Describe a time you demonstrated exceptional ${topic.toLowerCase()} and how the outcome aligns with the role expectations from the job specification.`,
        type,
        correctAnswer: `Look for structured storytelling, specific impact, and references to ${topic.toLowerCase()}.`,
        points: 15,
      };
    }

    return {
      id: baseId + index,
      text: `Outline the key steps you would take to deliver excellent ${topic.toLowerCase()} based on the responsibilities highlighted in the job specification.`,
      type,
      correctAnswer: `Candidates should mention measurable actions, collaboration, and alignment with ${topic.toLowerCase()}.`,
      points: 10,
    };
  });
}
